import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { cssMap, cx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { Box } from '@atlaskit/primitives/compiled/box';

import googleDriveIcon from './img/google-drive.svg';
import loomIcon from './img/loom.svg';
import onedriveIcon from './img/onedrive.svg';

const ICON_SIZE: Record<'small' | 'medium' | 'large', number> = {
	small: 16,
	medium: 20,
	large: 28,
};

const i18n = defineMessages({
	googleDriveIconAlt: {
		id: 'space-curation.space-navigation.google-drive-icon.alt',
		defaultMessage: 'Google Drive icon',
		description: 'Alt text for the Google Drive logo displayed next to the third-party auth prompt',
	},
	onedriveIconAlt: {
		id: 'space-curation.space-navigation.onedrive-icon.alt',
		defaultMessage: 'OneDrive icon',
		description: 'Alt text for the OneDrive logo displayed next to the third-party auth prompt',
	},
	loomIconAlt: {
		id: 'space-curation.space-navigation.loom-icon.alt',
		defaultMessage: 'Loom icon',
		description: 'Alt text for the Loom logo displayed next to the third-party auth prompt',
	},
});

const styles = cssMap({
	wrapper: {
		display: 'inline-flex',
		alignItems: 'center',
		flexShrink: 0,
		marginInlineEnd: token('space.100'),
		verticalAlign: 'top',
	},
	iconSlotFirst: {
		borderRadius: token('radius.small'),
		flexShrink: 0,
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		marginInlineStart: '0',
		position: 'relative',
		zIndex: 100,
		transform: 'rotate(-8deg)',
		border: `${token('border.width')} solid ${token('color.border')}`,
	},
	iconSlotSingle: {
		borderRadius: token('radius.small'),
		flexShrink: 0,
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		marginInlineStart: '0',
		position: 'relative',
		border: `${token('border.width')} solid ${token('color.border')}`,
	},
	iconSlotSecond: {
		borderRadius: token('radius.small'),
		flexShrink: 0,
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		marginInlineStart: token('space.negative.050'),
		position: 'relative',
		zIndex: 1,
		transform: 'rotate(8deg)',
		border: `${token('border.width')} solid ${token('color.border')}`,
	},
	iconSlotThird: {
		borderRadius: token('radius.small'),
		flexShrink: 0,
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		marginInlineStart: token('space.negative.050'),
		position: 'relative',
		zIndex: 0,
		border: `${token('border.width')} solid ${token('color.border')}`,
	},
});

export type IconKey = 'google-drive' | 'onedrive' | 'loom';

const ICON_META: Record<IconKey, { src: string; altKey: keyof typeof i18n }> = {
	'google-drive': { src: googleDriveIcon, altKey: 'googleDriveIconAlt' },
	onedrive: { src: onedriveIcon, altKey: 'onedriveIconAlt' },
	loom: { src: loomIcon, altKey: 'loomIconAlt' },
};

const SLOT_STYLES = [styles.iconSlotFirst, styles.iconSlotSecond, styles.iconSlotThird] as const;

type StackedContentIconsProps = {
	variant?: 'small' | 'medium' | 'large';
	testId?: string;
	/** Show a single icon without tilt. Mutually exclusive with `icons`. */
	singleIcon?: IconKey;
	/** Show 2–3 stacked icons in order (first is tilted left, second right, third flat). Mutually exclusive with `singleIcon`. */
	icons?: [IconKey, IconKey] | [IconKey, IconKey, IconKey];
};

export const StackedContentIcons = ({
	variant = 'small',
	testId = 'stacked-content-icons',
	singleIcon,
	icons,
}: StackedContentIconsProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const size = ICON_SIZE[variant];

	if (singleIcon) {
		const { src, altKey } = ICON_META[singleIcon];
		return (
			<Box xcss={styles.wrapper} testId={testId}>
				<Box xcss={styles.iconSlotSingle}>
					<img src={src} width={size} height={size} alt={formatMessage(i18n[altKey])} />
				</Box>
			</Box>
		);
	}

	const iconList = icons ?? (['google-drive', 'onedrive'] as [IconKey, IconKey]);

	return (
		<Box xcss={styles.wrapper} testId={testId}>
			{iconList.map((key, index) => {
				const { src, altKey } = ICON_META[key];
				return (
					<Box key={key} xcss={cx(SLOT_STYLES[index])}>
						<img src={src} width={size} height={size} alt={formatMessage(i18n[altKey])} />
					</Box>
				);
			})}
		</Box>
	);
};
